/**
 * FILE: italiansecret-subscription-sticky-cart.js
 * Parallel subscription test clone of italiansecret-sticky-cart.js.
 * It uses separate IDs, buttons, and session flags so the production sticky
 * cart modal remains untouched while Prometheus B2C flows are tested.
 *
 * Responsibilities:
 * - Open sticky cart modal from sticky bar
 * - Reload + reopen to keep Liquid cart in sync
 * - Handle qty +/- with reload
 * - Sync sticky bar summary
 *
 * NEW:
 * - Optionally auto-open Delivery Calendar modal after reload
 *   when sessionStorage flag italiansecret_open_delivery_modal == "1"
 * - Added by Italian Secret on 2026-05-11: sync selected delivery date to cart lines before checkout.
 */

(function () {
  const OPEN_FLAG_KEY = "italiansecret_open_subscription_sticky_cart";
  const OPEN_DELIVERY_MODAL_KEY = "italiansecret_open_delivery_modal";
  const DELIVERY_KEY = "italiansecret_delivery_date";

  // Added by Italian Secret on 2026-04-26: layered free-delivery status markers.
  const NUDGE_COOKIE_NAME = "is_returning_customer";
  const NUDGE_STORAGE_NAME = "italiansecret_returning_customer";
  const LEGACY_NUDGE_STORAGE_NAME = "is_returning_customer";
  const RETURNING_CUSTOMER_MARKER_DAYS = 365;

  function getCookieValue(name) {
    // Added by Italian Secret on 2026-01-16
    const escaped = name.replace(/([.*+?^${}()|[\]\\])/g, "\\$1");
    const match = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : "";
  }

  function setCookieValue(name, value, days) {
    const maxAge = Math.max(0, Math.round((days || 0) * 24 * 60 * 60));
    let cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
    if (window.location.protocol === "https:") cookie += "; Secure";
    document.cookie = cookie;
  }

  function getStorageValue(name) {
    try {
      return window.localStorage.getItem(name) || "";
    } catch (e) {
      return "";
    }
  }

  function setStorageValue(name, value) {
    try {
      window.localStorage.setItem(name, value);
    } catch (e) {}
  }

  function isTruthyMarker(value) {
    return value === "true" || value === "1" || value === "yes";
  }

  function hasReturningBrowserMarker() {
    if (
      window.ItalianSecretCustomerStatus &&
      typeof window.ItalianSecretCustomerStatus.hasReturningMarker === "function"
    ) {
      return window.ItalianSecretCustomerStatus.hasReturningMarker();
    }

    return (
      isTruthyMarker(getCookieValue(NUDGE_COOKIE_NAME)) ||
      isTruthyMarker(getStorageValue(NUDGE_STORAGE_NAME)) ||
      isTruthyMarker(getStorageValue(LEGACY_NUDGE_STORAGE_NAME))
    );
  }

  function rememberReturningBrowserMarker() {
    if (
      window.ItalianSecretCustomerStatus &&
      typeof window.ItalianSecretCustomerStatus.rememberReturningCustomer === "function"
    ) {
      window.ItalianSecretCustomerStatus.rememberReturningCustomer();
      return;
    }

    setCookieValue(NUDGE_COOKIE_NAME, "1", RETURNING_CUSTOMER_MARKER_DAYS);
    setStorageValue(NUDGE_STORAGE_NAME, "1");
    setStorageValue(LEGACY_NUDGE_STORAGE_NAME, "1");
  }

  function resolveNudgeStatus(wrapper) {
    const forceGuest = wrapper.dataset.nudgeForceGuest === "true";
    const serverStatus = wrapper.dataset.nudgeCustomerStatus || "unknown";

    if (
      window.ItalianSecretCustomerStatus &&
      typeof window.ItalianSecretCustomerStatus.resolveStatus === "function"
    ) {
      return window.ItalianSecretCustomerStatus.resolveStatus({
        forceGuest: forceGuest,
        serverStatus: serverStatus,
      });
    }

    if (forceGuest) return "new";
    if (serverStatus === "returning") rememberReturningBrowserMarker();
    if (serverStatus === "new" || serverStatus === "returning") return serverStatus;

    if (hasReturningBrowserMarker()) return "returning";

    return "unknown";
  }

  function updateNudge(wrapper) {
    if (!wrapper) return;
    const subtotalCents = parseInt(wrapper.dataset.nudgeSubtotal, 10) || 0;
    const newThreshold = parseInt(wrapper.dataset.nudgeNewThreshold, 10) || 0;
    const returningThreshold = parseInt(wrapper.dataset.nudgeReturningThreshold, 10) || newThreshold;
    const unknownThreshold = parseInt(wrapper.dataset.nudgeUnknownThreshold, 10) || newThreshold;
    const textNew = wrapper.dataset.nudgeTextNew || "";
    const textReturning = wrapper.dataset.nudgeTextReturning || textNew;
    const textUnknown = wrapper.dataset.nudgeTextUnknown || textNew;
    const rewardText = wrapper.dataset.nudgeRewardText || "";
    const rewardTextUnknown = wrapper.dataset.nudgeRewardTextUnknown || rewardText;
    const status = resolveNudgeStatus(wrapper);
    const threshold =
      status === "returning" ? returningThreshold :
      status === "unknown" ? unknownThreshold :
      newThreshold;
    const thresholdCents = Math.max(0, threshold * 100);

    if (!thresholdCents) return;

    const scaleMax = wrapper.querySelector("[data-nudge-scale-max]");
    if (scaleMax) scaleMax.textContent = threshold;

    const textEl = wrapper.querySelector("[data-nudge-text]");
    if (textEl) {
      textEl.textContent =
        status === "returning" ? textReturning :
        status === "unknown" ? textUnknown :
        textNew;
    }

    const progress = Math.min(100, Math.round((subtotalCents / thresholdCents) * 100));
    const fill = wrapper.querySelector("[data-nudge-fill]");
    const pin = wrapper.querySelector("[data-nudge-pin]");
    if (fill) fill.style.width = progress + "%";
    if (pin) pin.style.left = progress + "%";

    const reward = wrapper.querySelector("[data-nudge-reward]");
    const progressEl = wrapper.querySelector("[data-nudge-progress]");
    const rewardTextEl = wrapper.querySelector("[data-nudge-reward-text]");
    const showReward = subtotalCents >= thresholdCents;
    if (rewardTextEl) rewardTextEl.textContent = status === "unknown" ? rewardTextUnknown : rewardText;
    if (reward) reward.hidden = !showReward;
    if (progressEl) progressEl.hidden = showReward;
  }

  function onReady(fn) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn);
    } else {
      fn();
    }
  }

  onReady(function () {
    const overlay = document.getElementById("is-subscription-sticky-cart-overlay");
    const stickyBtn = document.querySelector("[data-is-subscription-sticky-button]");

    if (!overlay || !stickyBtn) {
      console.warn("SubscriptionStickyCart: overlay or button not found");
      return;
    }

    const nudgeWrapper = overlay.querySelector("[data-is-cart-nudge]");
    updateNudge(nudgeWrapper);

    function openCart() {
      overlay.classList.remove("is-sticky-cart--hidden");
      overlay.setAttribute("aria-hidden", "false");
      document.body.classList.add("is-sticky-cart-open");
    }

    function closeCart() {
      overlay.classList.add("is-sticky-cart--hidden");
      overlay.setAttribute("aria-hidden", "true");
      document.body.classList.remove("is-sticky-cart-open");
    }

    function openDeliveryModalIfRequested() {
      let flag = "0";
      try { flag = sessionStorage.getItem(OPEN_DELIVERY_MODAL_KEY) || "0"; } catch (e) {}

      if (flag !== "1") return;

      // consume flag
      try { sessionStorage.setItem(OPEN_DELIVERY_MODAL_KEY, "0"); } catch (e) {}

      const modal = document.getElementById("deliveryDateModal");
      if (!modal) return;

      modal.classList.add("is-open");
      document.body.classList.add("is-delivery-modal-open");
    }

    /* --------------------------------
       AUTO-OPEN AFTER RELOAD
    --------------------------------- */
    try {
      if (sessionStorage.getItem(OPEN_FLAG_KEY) === "1") {
        sessionStorage.removeItem(OPEN_FLAG_KEY);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const hasItems =
              overlay.querySelector(".is-sticky-cart__item") ||
              overlay.querySelector("[data-sticky-cart-item]");

            if (hasItems) {
              openCart();
              openDeliveryModalIfRequested();
            } else {
              setTimeout(() => {
                openCart();
                openDeliveryModalIfRequested();
              }, 120);
            }
          });
        });
      }
    } catch (e) {
      console.warn("SubscriptionStickyCart: cannot read open flag", e);
    }

    /* --------------------------------
       STICKY BAR BUTTON
    --------------------------------- */
    stickyBtn.addEventListener(
      "click",
      function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        try {
          sessionStorage.setItem(OPEN_FLAG_KEY, "1");
        } catch (err) {
          console.warn("SubscriptionStickyCart: cannot set open flag", err);
        }

        window.location.reload();
      },
      true
    );

    /* --------------------------------
       CLOSE HANDLERS
    --------------------------------- */
    overlay
      .querySelectorAll("[data-sticky-cart-close]")
      .forEach((el) => el.addEventListener("click", closeCart));

    overlay.addEventListener("click", function (e) {
      if (e.target === overlay) closeCart();
    });

    document.addEventListener("keydown", function (e) {
      if (
        e.key === "Escape" &&
        !overlay.classList.contains("is-sticky-cart--hidden")
      ) {
        closeCart();
      }
    });

    /* --------------------------------
       DELIVERY DATE (SESSION)
    --------------------------------- */
    const deliveryEl = document.getElementById("isSubscriptionStickyCartModalDelivery");
    const subscriptionSummaryEl = overlay.querySelector("[data-is-subscription-modal-summary]");

    function daySuffix(day) {
      if (day >= 11 && day <= 13) return "th";
      const last = day % 10;
      if (last === 1) return "st";
      if (last === 2) return "nd";
      if (last === 3) return "rd";
      return "th";
    }

    function formatDate(raw) {
      if (!raw) return "Not selected";
      const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!m) return raw;
      const d = new Date(+m[1], +m[2] - 1, +m[3]);
      const weekday = d.toLocaleDateString("en-GB", { weekday: "long" });
      const month = d.toLocaleDateString("en-GB", { month: "long" });
      const day = d.getDate();
      return `${weekday} ${day}${daySuffix(day)} ${month}`;
    }

    function updateDelivery() {
      if (!deliveryEl) return;
      let raw = "";
      try {
        raw = sessionStorage.getItem(DELIVERY_KEY) || "";
      } catch {}
      deliveryEl.textContent = formatDate(raw);
    }

    function updateSubscriptionSummary() {
      if (!subscriptionSummaryEl) return;
      let mode = "one_time";
      let optionId = "";
      try {
        mode = localStorage.getItem("italiansecret_subscription_mode") || "one_time";
        optionId = localStorage.getItem("italiansecret_subscription_option_id") || "";
      } catch (e) {}
      subscriptionSummaryEl.textContent = mode === "subscription"
        ? `Subscription${optionId ? " - " + optionId.replace(/[-_]+/g, " ") : ""}`
        : "One-off";
    }

    updateSubscriptionSummary();
    window.addEventListener("italiansecret:subscription-selection-changed", updateSubscriptionSummary);

    function getSelectedDeliveryIso() {
      if (
        window.ItalianSecretDelivery &&
        typeof window.ItalianSecretDelivery.ensureValidIso === "function"
      ) {
        return window.ItalianSecretDelivery.ensureValidIso();
      }

      try {
        return sessionStorage.getItem(DELIVERY_KEY) || "";
      } catch {
        return "";
      }
    }

    function syncDeliveryBeforeCheckout() {
      const iso = getSelectedDeliveryIso();
      if (
        iso &&
        window.ItalianSecretDelivery &&
        typeof window.ItalianSecretDelivery.syncCartLines === "function"
      ) {
        return window.ItalianSecretDelivery.syncCartLines(iso);
      }
      return Promise.resolve();
    }

    updateDelivery();
    window.addEventListener(
      "italiansecret:delivery-date-selected",
      updateDelivery
    );

    overlay
      .querySelectorAll('.is-sticky-cart__primary[href*="/checkout"]')
      .forEach((link) => {
        link.addEventListener("click", function (e) {
          e.preventDefault();
          const href = link.href || "/checkout";
          syncDeliveryBeforeCheckout()
            .catch(() => {})
            .then(() => {
              window.location.href = href;
            });
        });
      });

    /* --------------------------------
       QTY / REMOVE (RELOAD STRATEGY)
    --------------------------------- */
    function updateLine(line, qty) {
      fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ line: line, quantity: qty }),
      })
        .then(() => {
          sessionStorage.setItem(OPEN_FLAG_KEY, "1");
          window.location.reload();
        })
        .catch(() => {
          sessionStorage.setItem(OPEN_FLAG_KEY, "1");
          window.location.reload();
        });
    }

    overlay
      .querySelectorAll("[data-sticky-cart-qty]")
      .forEach((btn) => {
        btn.addEventListener("click", function () {
          const line = parseInt(btn.dataset.line, 10);
          const row = btn.closest(".is-sticky-cart__item");
          const val = row.querySelector(".is-sticky-cart__qty-value");
          const current = parseInt(val.textContent, 10) || 0;

          const dir = btn.dataset.stickyCartQty;
          const next = dir === "plus" ? current + 1 : Math.max(0, current - 1);
          updateLine(line, next);
        });
      });

    overlay
      .querySelectorAll("[data-sticky-cart-remove]")
      .forEach((btn) => {
        btn.addEventListener("click", function () {
          const line = parseInt(btn.dataset.line, 10);
          updateLine(line, 0);
        });
      });
  });
})();
